import styled from '@emotion/styled';
import * as React from 'react';
import { useRotatingFade } from '../../hooks/useRotatingFade';
import { PHONE_MEDIA } from '../../vars';

interface Capability {
  label: string;
  blurb: string;
}

const CAPABILITIES: Capability[] = [
  {
    label: 'Runtime',
    blurb: 'A common foundation for building apps that run consistently across devices and operating systems.'
  },
  {
    label: 'Sync',
    blurb: 'Offline-first data sync keeps apps fast, resilient and usable when connectivity drops away.'
  },
  {
    label: 'Backend',
    blurb: 'App backends, environments and operational controls are built in from day one.'
  },
  {
    label: 'Deployments',
    blurb:
      'Testing, staging and production flows are already part of the platform instead of extra infrastructure work.'
  },
  {
    label: 'CloudCode',
    blurb: 'Bring your own packages, integrations and custom logic into the platform without breaking your flow.'
  },
  {
    label: 'Data tools',
    blurb: 'Inspect, analyze and manage live application data with tools designed for real operational systems.'
  },
  {
    label: 'Observability',
    blurb:
      'Logs, change history and operational visibility are close at hand when you need to understand what is happening.'
  },
  {
    label: 'Modular',
    blurb: 'Start with an integrated stack, then extend the parts that matter most as your app grows.'
  },
  {
    label: 'Integrations',
    blurb: 'Connect existing services, APIs and workflows instead of replacing the rest of your stack.'
  },
  {
    label: 'AI',
    blurb: 'Well-shaped abstractions give AI clearer structure, making its outputs more predictable and useful.'
  }
];

const ROTATE_EVERY_MS = 4200;

export const HeroCapabilityRotator: React.FC = () => {
  const pillsRef = React.useRef<HTMLDivElement | null>(null);
  const pillRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const { activeIndex, isVisible, reduceMotion, setIsPaused, showIndex } = useRotatingFade({
    count: CAPABILITIES.length,
    rotateEveryMs: ROTATE_EVERY_MS
  });

  React.useEffect(() => {
    const pills = pillsRef.current;
    const activePill = pillRefs.current[activeIndex];
    if (!pills || !activePill) {
      return;
    }

    const targetLeft = activePill.offsetLeft - pills.clientWidth / 2 + activePill.clientWidth / 2;

    pills.scrollTo({
      left: Math.max(0, targetLeft),
      behavior: reduceMotion ? 'auto' : 'smooth'
    });
  }, [activeIndex, reduceMotion]);

  return (
    <S.Container onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
      <S.Label>Everything you need to build on a serious foundation:</S.Label>
      <S.Pills ref={pillsRef}>
        {CAPABILITIES.map((capability, index) => (
          <S.Pill
            key={capability.label}
            type="button"
            active={index === activeIndex}
            ref={(element) => {
              pillRefs.current[index] = element;
            }}
            onClick={() => showIndex(index)}
            aria-pressed={index === activeIndex}
          >
            {capability.label}
          </S.Pill>
        ))}
      </S.Pills>
      <S.Blurb visible={isVisible}>{CAPABILITIES[activeIndex].blurb}</S.Blurb>
    </S.Container>
  );
};

namespace S {
  export const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    width: 100%;
    overflow: hidden;
  `;

  export const Label = styled.div`
    color: ${(p) => p.theme.text.paragraph};
    font-size: 14px;
    line-height: 1.4;
  `;

  export const Pills = styled.div`
    display: flex;
    flex-wrap: nowrap;
    gap: 10px;
    overflow-x: auto;
    scrollbar-width: none;
    padding-bottom: 4px;
    margin-bottom: -4px;

    &::-webkit-scrollbar {
      display: none;
    }
  `;

  export const Pill = styled.button<{ active: boolean }>`
    appearance: none;
    border: 1px solid ${(p) => (p.active ? p.theme.text.heading : 'rgba(255, 255, 255, 0.12)')};
    background: ${(p) => (p.active ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.03)')};
    color: ${(p) => (p.active ? p.theme.text.heading : p.theme.text.paragraph)};
    border-radius: 999px;
    padding: 10px 14px;
    font-size: 14px;
    line-height: 1;
    white-space: nowrap;
    flex: 0 0 auto;
    cursor: pointer;

    &:hover {
      transition:
        border-color 180ms ease,
        background 180ms ease,
        color 180ms ease;
      border-color: ${(p) => p.theme.text.heading};
      color: ${(p) => p.theme.text.heading};
    }
  `;

  export const Blurb = styled.div<{ visible: boolean }>`
    min-height: 56px;
    color: #8f59ff;
    line-height: 1.55;
    font-size: 15px;
    opacity: ${(p) => (p.visible ? 1 : 0)};
    transform: translateY(${(p) => (p.visible ? 0 : 4)}px);
    transition:
      opacity 180ms ease,
      transform 180ms ease;

    @media (max-width: ${PHONE_MEDIA}px) {
      min-height: 72px;
    }
  `;
}
